// @owners { team: patients-team }
import { COLORS, SPACING } from '@alto/design-library-tokens';
import {
  ButtonSelector,
  Description,
  InlineAlert,
  LgPadding,
  type OptionValue,
  XsSpacing,
  generateInitialValue,
} from '@alto/design-system';
import { type ApiResponseRow } from '@alto/design-system/src/components/inputs/src/InputAutocomplete/types';
import { type AllergenParams } from '@alto/scriptdash/alto/wunderbar/patients/types/v1/allergen_params';
import React, { useState } from 'react';
import { type User } from '~shared/types';
import { AllergenSearchField } from './AllergenSearchInput';

type Props = {
  readonly allergies: string[];
  readonly error?: string;
  readonly familyMemberName?: string; // first name of family member
  readonly handleChange: (values: ApiResponseRow<string>[]) => void;
  readonly handleButtonChange: (isToggled: boolean) => void;
  readonly hasSubmitted?: boolean;
  readonly isTouched?: boolean;
  readonly fullBleed?: boolean;
};

export const NO_KNOWN_ALLERGIES = 'No Known Allergies';

const YES_VALUE = 'Yes';
const NO_VALUE = 'No';

const options = [
  { label: NO_VALUE, value: NO_VALUE, testID: 'allergies-no' },
  { label: YES_VALUE, value: YES_VALUE, testID: 'allergies-yes' },
];

const getDefaultValue = (hasAllergies: boolean) => {
  if (hasAllergies) {
    return YES_VALUE;
  }

  return NO_VALUE;
};

/**
 * Prepare a patient's allergies (free-form text) and allergens (structured JSON)
 * for the allergy search input. When a patient does not have any allergies, the value
 * "No Known Allergies" is saved in user.allergies and an allergen, NKA_ALLERGEN, is added
 * to user.allergens
 * @param user
 * @returns string[]
 */
export const getAllergensForInput = (user: User | null | undefined) => {
  const allergies = user?.allergies;
  const allergens = user?.allergens;
  if (allergies === NO_KNOWN_ALLERGIES) return [];

  const stringifiedValues = (allergens || []).map((allergen) => JSON.stringify(allergen));
  if (allergies) {
    allergies.split(',').forEach((allergy) => stringifiedValues.push(allergy));
  }
  return stringifiedValues;
};

const getInitialValues = (allergies: string[]) => {
  return allergies.map((allergy) => {
    try {
      const allergen: AllergenParams = JSON.parse(allergy); // structured allergens can be parsed
      return {
        ...generateInitialValue(allergen.description),
        isFromAutocomplete: true,
        value: allergy,
      };
    } catch {
      return generateInitialValue(allergy);
    }
  });
};

/**
 * AllergiesInput takes in data from user.allergies (free-form text) and
 * user.allergens (structured JSON). The data is converted to initial values
 * using `generateInitialValue`. An array of allergy strings can be parsed
 * on the BE. The strings are either saved as free-form text (user.allergies) or
 * structured JSON (user.allergens).
 * i.e. user.allergies = 'strawberries, cats'
 * i.e. user.allergens = [{id: 1, code: 123, group: 'medication', description: 'Penicillins' }]
 */
export const AllergiesInput = ({
  allergies,
  error,
  familyMemberName,
  handleChange,
  handleButtonChange,
  hasSubmitted = false,
  isTouched = false,
  fullBleed = false,
}: Props) => {
  const [hasAllergies, setHasAllergies] = useState(!!allergies && allergies.length > 0);
  const handleAllergies = (value: OptionValue) => {
    if (value === YES_VALUE) {
      setHasAllergies(true);
      handleButtonChange(true);
    } else {
      setHasAllergies(false);
      handleButtonChange(false);
      handleChange([]);
    }
  };

  const defaultValue = getDefaultValue(hasAllergies);
  const title = familyMemberName ? `Does ${familyMemberName} have any allergies?` : 'Do you have any allergies?';
  const LeftRightPadding = fullBleed ? SPACING.STATIC.NONE : SPACING.STATIC.LG;

  return (
    <>
      <LgPadding
        topPadding={SPACING.STATIC.NONE}
        bottomPadding={SPACING.STATIC.MD}
        leftPadding={LeftRightPadding}
        rightPadding={LeftRightPadding}
      >
        <Description
          color={COLORS.TEXT_COLORS.PRIMARY}
          fontFamily="semibold"
        >
          {title}
        </Description>
        <XsSpacing />
        <ButtonSelector
          options={options}
          onChange={handleAllergies}
          value={defaultValue}
        />
      </LgPadding>
      {hasAllergies ? (
        <LgPadding
          topPadding={SPACING.STATIC.NONE}
          leftPadding={LeftRightPadding}
          rightPadding={LeftRightPadding}
        >
          <AllergenSearchField
            familyMemberName={familyMemberName}
            isTouched={isTouched}
            hasSubmitted={hasSubmitted}
            onChange={handleChange}
            error={error ? error : undefined}
            initialValues={getInitialValues(allergies)}
          />
        </LgPadding>
      ) : null}
      {(isTouched || hasSubmitted) && !!error && defaultValue === undefined ? (
        <LgPadding
          topPadding={SPACING.STATIC.MD}
          bottomPadding={SPACING.STATIC.MD}
          leftPadding={LeftRightPadding}
          rightPadding={LeftRightPadding}
        >
          <InlineAlert type="error">
            <Description>{error}</Description>
          </InlineAlert>
        </LgPadding>
      ) : null}
    </>
  );
};
